import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, Send } from 'lucide-react';
import {
    useCreateGovernanceRecord,
    useUpdateGovernanceRecord,
    useUsers,
} from '../../hooks/useApi';
import { SectionFields } from './FormRenderer';
import ActionsEditor from './ActionsEditor';
import {
    Alert,
    Button,
    FormField,
    Input,
    Modal,
    Select,
    Tabs,
} from '../../components/ui';

const today = () => new Date().toISOString().split('T')[0];

const EMPTY_META = {
    title: '',
    rating: '',
    owner_department: 'ICT Department',
    raised_date: today(),
    target_date: '',
};

/**
 * Raising or editing a controlled form.
 *
 * Tabs rather than one long page: each form is short per section but has four
 * or five sections, so a scrolling page hid the fact that the record was
 * already nearly complete. Validation jumps to the tab holding the first
 * missing answer instead of just naming it.
 */
const RecordModal = ({ isOpen, onClose, form, record = null, ownerOptions = [] }) => {
    const navigate = useNavigate();
    const isEdit = Boolean(record);

    const createRecord = useCreateGovernanceRecord();
    const updateRecord = useUpdateGovernanceRecord();
    const { data: usersResult } = useUsers({ limit: 1000 });
    const users = usersResult?.data || [];

    const [tab, setTab] = useState('header');
    const [meta, setMeta] = useState(EMPTY_META);
    const [values, setValues] = useState({});
    const [actions, setActions] = useState([]);
    const [error, setError] = useState('');

    // Reset or hydrate whenever the dialog opens.
    useEffect(() => {
        if (!isOpen) return;
        setTab('header');
        setError('');

        if (record) {
            setMeta({
                title: record.title || '',
                rating: record.rating || '',
                owner_department: record.owner_department || 'ICT Department',
                raised_date: record.raised_date ? String(record.raised_date).split('T')[0] : today(),
                target_date: record.target_date ? String(record.target_date).split('T')[0] : '',
            });
            setValues(record.payload || {});
            setActions(record.actions || []);
        } else {
            setMeta(EMPTY_META);
            setValues({});
            setActions([]);
        }
    }, [isOpen, record]);

    const tabs = useMemo(() => {
        if (!form) return [];
        return [
            { value: 'header', label: 'Record header' },
            ...form.sections.map((s) => ({ value: s.key, label: s.title })),
            { value: 'actions', label: 'Gaps & actions', count: actions.length || undefined },
        ];
    }, [form, actions.length]);

    const setField = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

    const save = async (status) => {
        setError('');

        if (!meta.title.trim()) {
            setTab('header');
            setError('Give the record a title so it can be found in the register.');
            return;
        }

        if (status !== 'draft') {
            // Find the first section with an unanswered required field and land there.
            for (const section of form.sections) {
                const missing = section.fields.filter(
                    (f) => f.required && !String(values[f.key] || '').trim()
                );
                if (missing.length) {
                    setTab(section.key);
                    setError(
                        `${section.title}: complete ${missing.map((f) => f.label).join(', ')} before submitting.`
                    );
                    return;
                }
            }
        }

        const body = {
            ...meta,
            rating: meta.rating || null,
            target_date: meta.target_date || null,
            status,
            payload: values,
            actions,
        };

        try {
            if (isEdit) {
                await updateRecord.mutateAsync({ id: record.id, data: body });
                onClose();
            } else {
                const res = await createRecord.mutateAsync({ ...body, form_code: form.code });
                onClose();
                navigate(`/governance/${res.data.data.id}`);
            }
        } catch {
            /* the mutation surfaces its own error toast */
        }
    };

    if (!form) return null;

    const isSaving = createRecord.isPending || updateRecord.isPending;
    const activeSection = form.sections.find((s) => s.key === tab);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="xxl"
            title={isEdit ? `Edit ${record.record_ref}` : form.name}
            subtitle={`${form.code} · Version ${form.version} · Owner: ${form.owner}`}
            bodyClassName="pt-0"
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button icon={Save} onClick={() => save('draft')} loading={isSaving}>
                        Save draft
                    </Button>
                    <Button variant="primary" icon={Send} onClick={() => save('submitted')} loading={isSaving}>
                        Submit for approval
                    </Button>
                </>
            }
        >
            {/* Tabs stay put while the section below scrolls. */}
            <div className="sticky top-0 z-10 -mx-5 px-5 pt-3 bg-white">
                <Tabs tabs={tabs} value={tab} onChange={setTab} />
            </div>

            <div className="pt-4 min-h-[320px]">
                {error && (
                    <Alert variant="danger" className="mb-4">
                        {error}
                    </Alert>
                )}

                {tab === 'header' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField label="Title" required htmlFor="gv-title" className="md:col-span-2">
                            <Input
                                id="gv-title"
                                value={meta.title}
                                onChange={(e) => setMeta({ ...meta, title: e.target.value })}
                                placeholder="Short description used in the register"
                            />
                        </FormField>

                        <FormField label="Owner department" htmlFor="gv-dept">
                            <Select
                                id="gv-dept"
                                value={meta.owner_department}
                                onChange={(e) => setMeta({ ...meta, owner_department: e.target.value })}
                            >
                                {(ownerOptions.length ? ownerOptions : ['ICT Department']).map((o) => (
                                    <option key={o} value={o}>
                                        {o}
                                    </option>
                                ))}
                            </Select>
                        </FormField>

                        <FormField
                            label="Risk / criticality rating"
                            htmlFor="gv-rating"
                            hint="Drives the register's high-risk counter."
                        >
                            <Select
                                id="gv-rating"
                                value={meta.rating}
                                onChange={(e) => setMeta({ ...meta, rating: e.target.value })}
                            >
                                <option value="">Not rated</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                                <option value="critical">Critical</option>
                            </Select>
                        </FormField>

                        <FormField label="Date raised" htmlFor="gv-raised">
                            <Input
                                id="gv-raised"
                                type="date"
                                value={meta.raised_date}
                                onChange={(e) => setMeta({ ...meta, raised_date: e.target.value })}
                            />
                        </FormField>

                        <FormField label="Target / expiry date" htmlFor="gv-target">
                            <Input
                                id="gv-target"
                                type="date"
                                value={meta.target_date}
                                onChange={(e) => setMeta({ ...meta, target_date: e.target.value })}
                            />
                        </FormField>
                    </div>
                )}

                {activeSection && (
                    <>
                        {activeSection.hint && (
                            <p className="text-base text-ink-500 mb-4">{activeSection.hint}</p>
                        )}
                        <SectionFields
                            section={activeSection}
                            values={values}
                            onChange={setField}
                            users={users}
                        />
                    </>
                )}

                {tab === 'actions' && (
                    <ActionsEditor actions={actions} onChange={setActions} users={users} bare />
                )}
            </div>
        </Modal>
    );
};

export default RecordModal;
